import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  DialogContentText
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  PlaylistPlay as PlaylistPlayIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Stop as StopIcon,
  InfoOutlined as InfoIcon,
  VerifiedUser as VerifiedUserIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpOutlineIcon,
  ExpandMore as ExpandMoreIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { API_ENDPOINTS } from '../config/api';
import ConcurrencyLimitDialog from '../components/ConcurrencyLimitDialog';
import { usePermission } from '../hooks/usePermission';
import { useAuth } from '../hooks/useAuth';
import IntegrationSelector, { Integration } from '../components/IntegrationSelector';
import axios from 'axios';

interface PostReleaseTestConfig {
  enabled: boolean;
  description: string;
  testOrder: string[];
  testOrderDescription: Record<string, string>;
  autoTestOnRelease: boolean;
  testInterval: string;
  environmentMapping: Record<string, string>;
}

interface APKInfo { packageName: string; name: string; paymentSystem: string; versions: Record<string, any>; }

const PostReleaseTesting: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { hasPermission } = usePermission();
  const [config, setConfig] = useState<PostReleaseTestConfig | null>(null);
  const [apkList, setApkList] = useState<Record<string, APKInfo>>({}); // 保留但不再用于渲染
  const [runs, setRuns] = useState<any[]>([]);
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const [viewingRunId, setViewingRunId] = useState<string | null>(null); // 🎯 新增：当前正在查看的run ID（可能是历史run）
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [maxDevices, setMaxDevices] = useState<number>(2);
  const [serverConfig, setServerConfig] = useState<any>(null);
  const [integrationConcurrency, setIntegrationConcurrency] = useState<number>(1);
  const [continueOnFail, setContinueOnFail] = useState<boolean>(true);
  const [retries, setRetries] = useState<number>(1);
  // 🎯 新增：集成选择相关状态
  const [selectedIntegrations, setSelectedIntegrations] = useState<string[]>([]);
  const [availableIntegrations, setAvailableIntegrations] = useState<Integration[]>([]);
  const [startMode, setStartMode] = useState<'reuse'|'reject'|'allow'>('reuse');
  const [debounceWindowMs, setDebounceWindowMs] = useState<number>(1500);
  const [maxActiveRuns, setMaxActiveRuns] = useState<number>(2);
  const [autoValidate, setAutoValidate] = useState<boolean>(false);
  const [validationConcurrency, setValidationConcurrency] = useState<number>(5);
  const [logOpen, setLogOpen] = useState(false);
  const [logText, setLogText] = useState('');
  const [logTitle, setLogTitle] = useState('Logs');
  // 🎯 SQL验证相关状态
  const [validationDetailsOpen, setValidationDetailsOpen] = useState(false);
  const [selectedValidation, setSelectedValidation] = useState<any>(null);
  const [validationFilter, setValidationFilter] = useState<'all' | 'passed' | 'failed' | 'error' | 'no_order_id'>('all');
  const [orderIdSearch, setOrderIdSearch] = useState('');
  // 🎯 历史运行相关状态
  const [historicalRuns, setHistoricalRuns] = useState<any[]>([]);
  const [historicalReportOpen, setHistoricalReportOpen] = useState(false);
  const [selectedHistoricalRun, setSelectedHistoricalRun] = useState<any>(null);
  const [historicalRunsError, setHistoricalRunsError] = useState<string | null>(null); // 🎯 新增：历史运行加载错误状态
  
  // 🔐 密码确认相关状态
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [pendingAction, setPendingAction] = useState<'start_test' | 'save_config' | null>(null);
  const [globalPasswordConfig, setGlobalPasswordConfig] = useState<{enabled: boolean, password: string}>({enabled: false, password: ''});
  const [passwordConfigLoading, setPasswordConfigLoading] = useState(false);
  
  // 调试信息
  console.log('🔍 [PostReleaseTesting] 用户信息:', user);
  console.log('🔍 [PostReleaseTesting] 用户角色:', user?.role);
  console.log('🔍 [PostReleaseTesting] 密码保护状态:', globalPasswordConfig);
  
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testProgress, setTestProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedAPK, setSelectedAPK] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, 'pending' | 'running' | 'completed' | 'failed'>>({});
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [runBannerVisible, setRunBannerVisible] = useState(false);
  const [concurrencyDialogOpen, setConcurrencyDialogOpen] = useState(false);
  const [concurrencyDialogData, setConcurrencyDialogData] = useState<{
    maxActiveRuns: number;
    activeRunId: string;
    mode: 'reuse' | 'reject' | 'allow';
  } | null>(null);
  const bannerRunStatus = (runs && runs.length > 0 && runs[0]?.id === currentRunId) ? (runs[0]?.status || 'running') : 'running';
  const validationTriggeredRef = React.useRef<Record<string, boolean>>({});
  // Desired display/testing order
  const desiredOrder = ['production', 'beta', 'production-new'];
  const queuedByDevice = React.useMemo(() => {
    try {
      const latest = Array.isArray(runs) && runs.length > 0 ? runs[0] : null;
      const items: any[] = latest?.items || [];
      const map: Record<string, number> = {};
      const locks = (latest as any)?.deviceLocks || {};
      // 仅统计“被其他 run 持有的锁”导致的排队；如果是本 run 拿到锁，不提示
      items.forEach((it: any) => {
        if (it.status === 'queued') {
          const owner = locks[it.deviceId];
          if (owner && owner !== latest?.id) {
            map[it.deviceId] = (map[it.deviceId] || 0) + 1;
          }
        }
      });

      // 若没有 queued 但设备被其他 run 占用，给出占用提示（计为1）
      Object.keys(locks).forEach((dev: string) => {
        const owner = locks[dev];
        if (owner && owner !== latest?.id && !map[dev]) map[dev] = 1;
      });
      return map;
    } catch {
      return {} as Record<string, number>;
    }
  }, [runs]);

  useEffect(() => {
    loadData();
    loadHistoricalRuns(); // 🎯 加载历史运行
    
    // 🎯 定期刷新历史运行数据（每30秒）
    const interval = setInterval(() => {
      loadHistoricalRuns();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // 🎯 恢复运行：URL ?run=<id>
    try {
      // 🔧 修复：HashRouter模式下，query参数在hash中
      // URL格式: http://.../#/post-release-testing?run=xxx&scrollTo=device-integration-env
        const hash = window.location.hash; // 例如: #/post-release-testing?run=xxx&scrollTo=xxx
        const queryStart = hash.indexOf('?');
        if (queryStart !== -1) {
          const queryString = hash.substring(queryStart + 1); // run=xxx&scrollTo=xxx
          const params = new URLSearchParams(queryString);
          const rid = params.get('run');
          const scrollTo = params.get('scrollTo');
          
          if (rid) {
            // 🎯 从Slack/URL打开run时，设置viewingRunId
            setViewingRunId(rid);
            setRunBannerVisible(false);
            setTestDialogOpen(true);
            pollRun(rid);
            console.log(`✅ [PostRelease] 从Slack链接打开Run: ${rid}`);
            // 🎯 scrollTo参数会在useEffect中处理（监听runs变化）
          }
        }
      } catch (err) {
        console.error('❌ [PostRelease] 解析run参数失败:', err);
      }
  }, []);

  // 🔐 密码确认相关函数
  const handlePasswordConfirm = async () => {
    if (!pendingAction) return;
    
    try {
      // 使用全局密码保护配置进行验证
      const correctPassword = globalPasswordConfig.password || 'post-release-2024';
      
      if (passwordInput === correctPassword) {
        setPasswordDialogOpen(false);
        setPasswordInput('');
        setPasswordError('');
        
        // 执行待处理的操作
        if (pendingAction === 'start_test') {
          await startPostReleaseTest();
        } else if (pendingAction === 'save_config') {
          await saveConfig();
        }
        
        setPendingAction(null);
      } else {
        setPasswordError('密码错误，请重试');
      }
    } catch (error) {
      console.error('密码验证失败:', error);
      setPasswordError('验证失败，请重试');
    }
  };

  const handleStartTestWithPassword = () => {
    if (globalPasswordConfig.enabled) {
      setPendingAction('start_test');
      setPasswordDialogOpen(true);
    } else {
      startPostReleaseTest();
    }
  };

  const saveConfig = async () => {
    try {
      console.log('🔧 [DEBUG] Saving options with selectedDevices:', selectedDevices);
      const payload = {
        startMode,
        debounceWindowMs,
        maxActiveRuns,
        // 新增持久化字段
        maxDevices,
        integrationConcurrency,
        continueOnFail,
        retries,
        autoValidate,
        validationConcurrency,
        preferredDevices: (selectedDevices && selectedDevices.length ? selectedDevices : undefined)
      };
      console.log('🔧 [DEBUG] Save payload:', payload);
      const response = await axios.post(`${API_ENDPOINTS.API_BASE}/api/post-release/config`, payload);
      console.log('🔧 [DEBUG] Save response:', response.data);
      // 保存后重新拉取一次配置回填
      await loadData();
      console.log('🔧 [DEBUG] Options saved successfully');
    } catch (e) { 
      console.error('🔧 [DEBUG] Save failed:', e); 
    }
  };

  const handleSaveConfigWithPassword = () => {
    if (globalPasswordConfig.enabled) {
      setPendingAction('save_config');
      setPasswordDialogOpen(true);
    } else {
      saveConfig();
    }
  };

  // 🔐 加载密码保护配置
  const loadPasswordConfig = async () => {
    try {
      setPasswordConfigLoading(true);
      const response = await fetch(`${API_ENDPOINTS.API_BASE}/api/post-release/password-config`);
      if (response.ok) {
        const data = await response.json();
        setGlobalPasswordConfig(data.config);
        console.log('🔐 [PostReleaseTesting] 密码保护配置已加载:', data.config);
      }
    } catch (error) {
      console.error('❌ [PostReleaseTesting] 加载密码保护配置失败:', error);
    } finally {
      setPasswordConfigLoading(false);
    }
  };

  // 🔐 更新密码保护配置
  const updatePasswordConfig = async (config: {enabled: boolean, password: string}) => {
    try {
      setPasswordConfigLoading(true);
      const response = await fetch(`${API_ENDPOINTS.API_BASE}/api/post-release/password-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Role': user?.role || 'reviewer'
        },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        const data = await response.json();
        setGlobalPasswordConfig(data.config);
        console.log('🔐 [PostReleaseTesting] 密码保护配置已更新:', data.config);
        return true;
      } else {
        const error = await response.json();
        console.error('❌ [PostReleaseTesting] 更新密码保护配置失败:', error);
        return false;
      }
    } catch (error) {
      console.error('❌ [PostReleaseTesting] 更新密码保护配置失败:', error);
      return false;
    } finally {
      setPasswordConfigLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [configResponse, apkResponse, prConfig] = await Promise.all([
        fetch(`${API_ENDPOINTS.APK_VERSIONS}/post-release-testing`),
        fetch(API_ENDPOINTS.APK_VERSIONS),
        fetch(`${API_ENDPOINTS.API_BASE}/api/post-release/config`, {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        })
      ]);
      
      const configData = await configResponse.json();
      const apkData = await apkResponse.json();
      
      setConfig(configData);
      setApkList(apkData);
      const pr = await prConfig.json();
      setServerConfig(pr);
      // 回填并发与策略
      setMaxDevices(pr?.concurrency?.maxDevices || 2);
      setIntegrationConcurrency(pr?.concurrency?.integrationConcurrency || 1);
      setMaxActiveRuns(pr?.concurrency?.maxActiveRuns || 2);
      if (pr?.policy?.startMode) setStartMode(pr.policy.startMode);
      if (typeof pr?.policy?.debounceWindowMs === 'number') setDebounceWindowMs(pr.policy.debounceWindowMs);
      if (typeof pr?.policy?.continueOnFail === 'boolean') setContinueOnFail(pr.policy.continueOnFail);
      if (typeof pr?.policy?.retries === 'number') setRetries(pr.policy.retries);
      if (typeof pr?.validation?.autoValidate === 'boolean') setAutoValidate(Boolean(pr.validation.autoValidate));
      if (typeof pr?.validation?.concurrency === 'number') setValidationConcurrency(pr.validation.concurrency);
      // 加载保存的设备选择
      console.log('🔧 [DEBUG] PostRelease Config:', pr);
      console.log('🔧 [DEBUG] PreferredDevices:', pr?.preferredDevices);
      if (pr?.preferredDevices && Array.isArray(pr.preferredDevices) && pr.preferredDevices.length > 0) {
        console.log('🔧 [DEBUG] Setting selectedDevices to:', pr.preferredDevices);
        setSelectedDevices(pr.preferredDevices);
      } else {
        console.log('🔧 [DEBUG] No preferredDevices found or invalid format');
      }
      
      // 🎯 加载集成选择配置
      if (pr?.integrationSelection) {
        const integrationConfig = pr.integrationSelection;
        const integrations: Integration[] = Object.entries(integrationConfig.availableIntegrations || {}).map(([id, info]: [string, any]) => ({
          id,
          label: info.label || id,
          description: info.description || '',
          devices: info.devices || []
        }));
        setAvailableIntegrations(integrations);
        
        // 设置默认选中的集成
        if (integrationConfig.enabled && integrationConfig.defaultSelected) {
          setSelectedIntegrations(integrationConfig.defaultSelected);
          console.log('🎯 [DEBUG] 默认选中的集成:', integrationConfig.defaultSelected);
        } else {
          // 如果没有默认配置，默认全选
          setSelectedIntegrations(integrations.map(i => i.id));
          console.log('🎯 [DEBUG] 默认全选所有集成');
        }
      }
      
      // 不再默认勾选设备，避免用户未选择时误启动
      
      // 🔐 加载密码保护配置
      await loadPasswordConfig();
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // 🎯 加载历史运行数据
  const loadHistoricalRuns = async () => {
    try {
      // 🎯 清除之前的错误状态
      setHistoricalRunsError(null);
      
      console.log('🔄 [Frontend] 正在加载历史运行数据... (v3.0)');
      const url = `${API_ENDPOINTS.API_BASE}/api/post-release/runs?limit=200&t=${Date.now()}`;
      console.log('📡 [Frontend] 请求URL:', url);
      console.log('📡 [Frontend] API_BASE:', API_ENDPOINTS.API_BASE);
      
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Accept': 'application/json'
        },
        credentials: 'include' // 确保发送cookies（如果需要）
      });
      
      console.log('📡 [Frontend] 响应状态:', response.status, response.statusText);
      console.log('📡 [Frontend] 响应头 Content-Type:', response.headers.get('Content-Type'));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [Frontend] API响应错误:', response.status, errorText);
        setHistoricalRuns([]);
        setHistoricalRunsError(`API错误: ${response.status} ${response.statusText}`);
        return;
      }
      
      // 检查Content-Type
      const contentType = response.headers.get('Content-Type');
      if (!contentType || !contentType.includes('application/json')) {
        console.warn('⚠️ [Frontend] 响应Content-Type不是JSON:', contentType);
      }
      
      const data = await response.json();
      console.log('📊 [Frontend] 原始数据:', {
        type: typeof data,
        isArray: Array.isArray(data),
        length: Array.isArray(data) ? data.length : 'N/A',
        sample: Array.isArray(data) ? data.slice(0, 3)?.map((r: any) => ({ id: r.id, status: r.status, startedAt: r.startedAt })) : data
      });
      
      // 确保 data 是数组
      if (!Array.isArray(data)) {
        console.error('❌ [Frontend] 返回的数据不是数组:', typeof data, data);
        // 尝试转换
        if (data && typeof data === 'object') {
          console.warn('⚠️ [Frontend] 尝试从对象中提取数组...');
          const possibleArray = (data as any).runs || (data as any).data || (data as any).items || [];
          if (Array.isArray(possibleArray)) {
            console.log('✅ [Frontend] 从对象中提取到数组，长度:', possibleArray.length);
            setHistoricalRuns(possibleArray);
            setHistoricalRunsError(null); // 成功时清除错误
            return;
          }
        }
        setHistoricalRuns([]);
        setHistoricalRunsError('返回的数据格式不正确');
        return;
      }
      
      // 按时间倒序排列，最新的在前面
      const sorted = data.sort((a: any, b: any) => {
        const aTime = new Date(a.startedAt || 0).getTime();
        const bTime = new Date(b.startedAt || 0).getTime();
        return bTime - aTime; // 最新的在前
      });
      
      console.log('✅ [Frontend] 历史运行数据已更新，共', sorted.length, '条');
      console.log('📋 [Frontend] 前3条数据:', sorted.slice(0, 3).map((r: any) => ({ id: r.id, status: r.status, startedAt: r.startedAt })));
      
      setHistoricalRuns(sorted); // 显示后端返回（最多200条）
      setHistoricalRunsError(null); // 成功时清除错误
    } catch (error: any) {
      console.error('❌ [Frontend] 加载历史运行数据失败:', error);
      console.error('错误详情:', error.message, error.stack);
      console.error('错误类型:', error.name);
      setHistoricalRuns([]); // 出错时清空，避免显示旧数据
      // 🎯 设置错误消息，但使用更友好的提示
      const errorMessage = error.message || 'Failed to fetch';
      setHistoricalRunsError(errorMessage.includes('Failed to fetch') ? '无法连接到服务器，请检查网络连接或服务器状态' : errorMessage);
    }
  };

  const startPostReleaseTest = async (devices?: string[]) => {
    // 🎯 关键修复：清理URL中的旧run参数，避免从历史run界面启动时状态混乱
    try {
      const currentUrl = new URL(window.location.href);
      const hash = window.location.hash;
      if (hash.includes('?run=')) {
        // 清理hash中的run参数
        const cleanHash = hash.split('?')[0];
        window.history.replaceState({}, '', currentUrl.origin + currentUrl.pathname + cleanHash);
        console.log('🧹 [PostRelease] 已清理URL中的旧run参数');
      }
    } catch (e) {
      console.warn('清理URL参数失败:', e);
    }
    
    // 🎯 重置viewingRunId，避免显示历史run
    setViewingRunId(null);
    
    // 🎯 关键修复：清空旧的run数据，避免对话框显示历史run
    setRuns([]);
    
    setSelectedAPK('post-release');
    setTestDialogOpen(true);
    setTesting(true);
    setTestProgress(0);
    setCurrentStep(0);
    
    // 初始化测试结果
    const initialResults: Record<string, 'pending' | 'running' | 'completed' | 'failed'> = {};
    config?.testOrder.forEach(version => {
      initialResults[version] = 'pending';
    });
    setTestResults(initialResults);

    try {
      const payload: any = { maxDevices, integrationConcurrency, continueOnFail, retries };
      const devs = devices && devices.length ? devices : selectedDevices;
      if (devs.length) payload.devices = devs;
      
      // 🎯 添加选中的集成列表
      if (selectedIntegrations && selectedIntegrations.length > 0) {
        payload.integrations = selectedIntegrations;
        console.log('🎯 [startPostReleaseTest] 选中的集成:', selectedIntegrations);
      }
      
      const { data } = await axios.post(`${API_ENDPOINTS.API_BASE}/api/post-release/run`, payload, { headers: { 'X-User-Id': (localStorage.getItem('userId') || 'web-ui') } }).catch(async (err) => {
        // 若设备被占用，不弹窗，直接附着到已有运行
        if (err?.response?.status === 409 && err?.response?.data?.error === 'device_locked') {
          try {
            const { data: runsAll } = await axios.get(`${API_ENDPOINTS.API_BASE}/api/post-release/run`);
            const active = Array.isArray(runsAll) ? runsAll.reverse().find((r: any) => r.status === 'running' || r.status === 'pending') : null;
            if (active?.id) {
              setCurrentRunId(active.id);
              setViewingRunId(active.id); // 🎯 同时设置viewingRunId
              setRunBannerVisible(true);
              setTestDialogOpen(true);
              
              // 🎯 修复：更新URL到活跃的runId（HashRouter模式）
              try {
                const currentUrl = new URL(window.location.href);
                const hash = window.location.hash.split('?')[0];
                const newHash = `${hash}?run=${active.id}`;
                window.history.replaceState({}, '', currentUrl.origin + currentUrl.pathname + newHash);
                console.log(`🔗 [PostRelease] 设备被占用，附着到活跃run: ${newHash}`);
              } catch (e) {
                console.warn('更新URL失败:', e);
              }
              
              pollRun(active.id);
              return { data: { runId: active.id } } as any;
            }
          } catch {}
        }
        
        // 并发限制达到上限的处理
        if (err?.response?.status === 429 && err?.response?.data?.error === 'concurrent_limit_reached') {
          const activeRunId = err?.response?.data?.activeRunId;
          const maxRuns = err?.response?.data?.maxActiveRuns;
          
          // 显示漂亮的双语对话框
          setConcurrencyDialogData({
            maxActiveRuns: maxRuns,
            activeRunId: activeRunId,
            mode: startMode
          });
          setConcurrencyDialogOpen(true);
          
          // 停止测试状态，等待用户选择
          setTesting(false);
          return; // 直接返回，不继续执行
        }
        
        throw err;
      });
      setCurrentRunId(data.runId);
      setViewingRunId(data.runId); // 🎯 同时设置viewingRunId
      setRunBannerVisible(true);
      // 🎯 修复：将 runId 写入hash的query参数（HashRouter模式）
      try {
        const currentUrl = new URL(window.location.href);
        const hash = window.location.hash.split('?')[0]; // 获取干净的hash路径
        const newHash = `${hash}?run=${data.runId}`;
        window.history.replaceState({}, '', currentUrl.origin + currentUrl.pathname + newHash);
        console.log(`🔗 [PostRelease] 已更新URL: ${newHash}`);
      } catch (e) {
        console.warn('更新URL失败:', e);
      }
      // 拉取进度
      pollRun(data.runId);
    } catch (error) {
      console.error('Post-release test failed:', error);
      setTesting(false);
    }
  };

  // 处理并发限制对话框的操作
  const handleConcurrencyDialogAttach = () => {
    if (concurrencyDialogData) {
      try {
        // 附着到活跃的运行
        setCurrentRunId(concurrencyDialogData.activeRunId);
        setViewingRunId(concurrencyDialogData.activeRunId); // 🎯 同时设置viewingRunId
        setRunBannerVisible(true);
        setTestDialogOpen(true);
        
        // 🔥 关键修复：设置 testing=true，允许点击外部最小化
        setTesting(true);
        
        // 🎯 修复：更新URL到活跃的runId（HashRouter模式）
        try {
          const currentUrl = new URL(window.location.href);
          const hash = window.location.hash.split('?')[0];
          const newHash = `${hash}?run=${concurrencyDialogData.activeRunId}`;
          window.history.replaceState({}, '', currentUrl.origin + currentUrl.pathname + newHash);
          console.log(`🔗 [PostRelease] 附着到活跃run，已更新URL: ${newHash}`);
        } catch (e) {
          console.warn('更新URL失败:', e);
        }
        
        pollRun(concurrencyDialogData.activeRunId);
      } catch (attachError) {
        console.error('Failed to attach to active run:', attachError);
      }
    }
    setConcurrencyDialogOpen(false);
    setConcurrencyDialogData(null);
  };

  const pollRun = async (runId: string) => {
    // 优先用 SSE 流；失败则退回轮询
    try {
      const es = new EventSource(`${API_ENDPOINTS.API_BASE}/api/post-release/run/${runId}/stream`);
      // 连接超时兜底：若3秒内没有任何数据，主动触发一次回退轮询
      const bootstrapTimeout = setTimeout(() => {
        try { es.close(); } catch {}
        fallbackPolling(runId);
      }, 3000);
      es.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          setRuns([data]);
          clearTimeout(bootstrapTimeout);
          
          // 🎯 滚动逻辑已在useEffect中处理（监听runs变化）
          
          if (data?.status === 'completed' || data?.status === 'failed') {
            // 运行结束后，若启用自动 SQL 校验，则尝试触发
            maybeTriggerValidation(data).catch(()=>{});
            es.close();
            setTesting(false);
            setRunBannerVisible(false);
            // 🎯 修复：清理hash中的run参数（HashRouter模式）
            try { 
              const currentUrl = new URL(window.location.href);
              const hash = window.location.hash.split('?')[0]; // 只保留路径，移除query参数
              window.history.replaceState({}, '', currentUrl.origin + currentUrl.pathname + hash);
              console.log('🧹 [PostRelease] 测试完成，已清理URL run参数');
            } catch (e) {
              console.warn('清理URL参数失败:', e);
            }
          }
        } catch {}
      };
      es.addEventListener('end', () => { clearTimeout(bootstrapTimeout); es.close(); setTesting(false); });
      es.onerror = () => { es.close(); fallbackPolling(runId); };
    } catch {
      fallbackPolling(runId);
    }
  };

  const fallbackPolling = async (runId: string) => {
    let finished = false;
    let attempts = 0;
    while (!finished) {
      try {
        const { data } = await axios.get(`${API_ENDPOINTS.API_BASE}/api/post-release/run/${runId}`);
        setRuns([data]);
        
        // 🎯 滚动逻辑已在useEffect中处理（监听runs变化）
        
        finished = data?.status === 'completed' || data?.status === 'failed';
        if (finished) {
          await maybeTriggerValidation(data);
        }
      } catch (err) {
        // 网络异常时，尝试有限次重试，避免未捕获异常导致崩溃
        attempts += 1;
        console.error('fallbackPolling network error, attempt', attempts, err);
        if (attempts >= 5) {
          // 放弃并结束对话框，用户可重试
          setTesting(false);
          break;
        }
      }
      await new Promise(r => setTimeout(r, 2000));
    }
    setTesting(false);
  };

  // 🎯 监听runs数据变化，当有数据且URL中有scrollTo参数时，滚动到对应执行框
  useEffect(() => {
    if (runs.length > 0) {
      const hash = window.location.hash;
      const queryStart = hash.indexOf('?');
      if (queryStart !== -1) {
        const queryString = hash.substring(queryStart + 1);
        const params = new URLSearchParams(queryString);
        const scrollTo = params.get('scrollTo');
        if (scrollTo) {
          // 延迟滚动，确保DOM已渲染
          setTimeout(() => {
            const itemId = `item-${scrollTo}`;
            const element = document.getElementById(itemId);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              console.log(`📍 [PostRelease] 已滚动到执行框: ${itemId}`);
            } else {
              console.warn(`⚠️ [PostRelease] 未找到执行框: ${itemId}`);
            }
          }, 800); // 延迟800ms确保对话框和内容已渲染
        }
      }
    }
  }, [runs]); // 当runs数据更新时触发

  // 运行结束后按环境触发 SQL 校验（后端也会触发；此处作为前端兜底与显式动作）
  const maybeTriggerValidation = async (runSnapshot: any) => {
    try {
      if (!autoValidate) return; // 仅在开启时触发
      const runId = runSnapshot?.id;
      if (!runId || validationTriggeredRef.current[runId]) return;
      validationTriggeredRef.current[runId] = true;

      const { data: ids } = await axios.get(`${API_ENDPOINTS.API_BASE}/api/post-release/run/${runId}/order-ids`);
      const byEnv: Record<string, string[]> = ids?.byEnvironment || {};
      const tasks: Promise<any>[] = [];
      Object.entries(byEnv).forEach(([env, orderIds]) => {
        if (Array.isArray(orderIds) && orderIds.length > 0) {
          tasks.push(axios.post(`${API_ENDPOINTS.API_BASE}/api/validation/run`, {
            env,
            orderIds,
            runId, // 🎯 传递runId，用于写回验证结果
            options: { concurrency: validationConcurrency }
          }).catch(()=>undefined));
        }
      });
      await Promise.all(tasks);
    } catch (e) {
      // 静默失败，避免打断主流程
      console.warn('auto validation trigger failed', e);
    }
  };

  // 根据最新的运行数据推导进度与步骤状态
  useEffect(() => {
    try {
      const latest = Array.isArray(runs) && runs.length > 0 ? runs[0] : null;
      const items: any[] = latest?.items || [];
      // 1) 百分比：基于条目维度（更细粒度）
      if (items.length > 0) {
        const completedCount = items.filter((it: any) => it.status === 'completed').length;
        const percent = Math.round((completedCount / items.length) * 100);
        if (!Number.isNaN(percent)) setTestProgress(percent);
      }

      // 2) 步骤：按配置的 testOrder 聚合各环境状态
      if (config?.testOrder && config.testOrder.length > 0) {
        const resultMap: Record<string, 'pending' | 'running' | 'completed' | 'failed'> = {};
        let completedSteps = 0;
        let runningStepIndex = -1;

        config.testOrder.forEach((env, idx) => {
          const envItems = items.filter((it: any) => it.environment === env);
          let status: 'pending' | 'running' | 'completed' | 'failed' = 'pending';
          if (envItems.length > 0) {
            const hasRunning = envItems.some((it: any) => it.status === 'running' || it.status === 'queued');
            const allCompleted = envItems.every((it: any) => it.status === 'completed');
            const hasFailed = envItems.some((it: any) => it.status === 'failed' || it.status === 'stopped' || it.status === 'error');
            if (allCompleted) status = 'completed';
            else if (hasRunning) status = 'running';
            else if (hasFailed) status = 'failed';
            else status = 'pending';
          }
          resultMap[env] = status;
          if (status === 'completed') completedSteps += 1;
          if (status === 'running' && runningStepIndex === -1) runningStepIndex = idx;
        });

        // 当前步骤 = 已完成 + 是否有运行中的步骤
        const step = completedSteps + (runningStepIndex >= 0 ? 1 : 0);
        setTestResults(resultMap);
        setCurrentStep(step);
      }
    } catch (e) {
      // 推导失败时不阻断UI
      // console.warn('derive progress failed', e);
    }
  }, [runs, config]);

  const stopTest = async () => {
    // 🎯 立即给用户反馈，不等待后端响应
    setTesting(false);
    setTestDialogOpen(false);
    
    try {
      if (currentRunId) {
        // 🎯 异步调用，不阻塞UI关闭
        axios.post(`${API_ENDPOINTS.API_BASE}/api/post-release/run/${currentRunId}/stop`, { 
          owner: (localStorage.getItem('userId') || 'web-ui') 
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: true // 🎯 修复：确保发送 cookies
        }).then(() => {
          console.log('✅ [PostRelease] 测试已停止');
        }).catch((e: any) => {
          console.error('❌ [PostRelease] 停止测试失败:', e);
          // 🎯 修复：显示错误提示
          const errorMessage = e?.response?.data?.error || e?.message || 'Failed to fetch';
          console.error(`   错误详情: ${errorMessage}`);
        });
        
        // 立即显示banner，持续监听状态
        setRunBannerVisible(true);
        pollRun(currentRunId);
      }
    } catch (e: any) { 
      console.error('❌ [PostRelease] stopTest 异常:', e);
      const errorMessage = e?.message || e?.toString() || 'Failed to fetch';
      console.error(`   错误详情: ${errorMessage}`);
    }
    
    // 清理状态
    setTestProgress(0);
    setCurrentStep(0);
    setTestResults({});
  };

  const getStepStatus = (version: string, index: number) => {
    if (index < currentStep) {
      return testResults[version] === 'completed' ? 'completed' : 'failed';
    }
    if (index === currentStep) {
      return testResults[version] === 'running' ? 'running' : 'pending';
    }
    return 'pending';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'running':
        return <CircularProgress size={20} />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <WarningIcon color="disabled" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
        return 'primary';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="flex-start" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('postReleaseTesting.title')}
        </Typography>
      </Box>

      {config && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('postReleaseTesting.configuration')}
          </Typography>
          <Typography variant="body2">
            {t('postReleaseTesting.description')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('postReleaseTesting.testOrder')}: {desiredOrder.join(' → ')}
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3} alignItems="stretch">
        {/* 测试顺序说明 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('postReleaseTesting.testOrderDescription')}
              </Typography>
              <Stepper orientation="vertical" activeStep={-1}>
                {desiredOrder.map((version, index) => (
                  <Step key={version}>
                    <StepLabel>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getStatusIcon(getStepStatus(version, index))}
                        {version.toUpperCase()} {t('postReleaseTesting.version')}
                      </Box>
                    </StepLabel>
                    <StepContent>
                      <Typography variant="body2" color="text.secondary">
                        {config?.testOrderDescription?.[version] || ''}
                      </Typography>
                      <Chip
                        label={getStepStatus(version, index)}
                        color={getStatusColor(getStepStatus(version, index)) as any}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        </Grid>

        {/* 环境映射 */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('postReleaseTesting.environmentMapping')}
              </Typography>
              <List sx={{ mt: -1 }}>
                <ListItem sx={{ alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ minWidth: 0, mr: 1, mt: 0.5 }}>
                    <Chip label="production" color="success" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="production → formal"
                    secondary="Third highest version, also used in production environment as formal version"
                  />
                </ListItem>
                <ListItem sx={{ alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ minWidth: 0, mr: 1, mt: 0.5 }}>
                    <Chip label="beta" color="warning" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="beta → prod-beta"
                    secondary="Second highest version, also used in production environment beta version"
                  />
                </ListItem>
                <ListItem sx={{ alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ minWidth: 0, mr: 1, mt: 0.5 }}>
                    <Chip label="production-new" color="primary" size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="production-new → pre-beta"
                    secondary="Highest version, also used in production environment as pre-beta version after release"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* APK列表 */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 6px 12px rgba(0,0,0,.06)' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                {t('postReleaseTesting.selectAPKForTest')}
              </Typography>
              {/* 🔍 调试信息 - 临时显示 */}
              <Box mb={2} p={2} sx={{ backgroundColor: '#fff3cd', borderRadius: 2, border: '1px solid #ffeaa7' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#856404' }}>
                  🔍 {t('postReleaseTesting.debugInfo.title', 'Debug Information')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('postReleaseTesting.debugInfo.user', 'User')}: {user ? `${user.name} (${user.role})` : t('postReleaseTesting.debugInfo.notLoggedIn', 'Not logged in')} | 
                  {t('postReleaseTesting.debugInfo.passwordProtection', 'Password Protection')}: {globalPasswordConfig.enabled ? t('postReleaseTesting.debugInfo.enabled', 'Enabled') : t('postReleaseTesting.debugInfo.disabled', 'Disabled')} | 
                  {t('postReleaseTesting.debugInfo.password', 'Password')}: {globalPasswordConfig.password ? '***' : t('postReleaseTesting.debugInfo.notSet', 'Not set')}
                </Typography>
              </Box>

              {/* 🔐 密码保护设置区域 - 仅Admin可见 */}
              {user?.role === 'admin' && (
                <Box mb={2} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#1976d2' }}>
                    🔐 {t('postReleaseTesting.passwordProtection.title', 'Password Protection Settings')} ({t('postReleaseTesting.passwordProtection.adminOnly', 'Admin Only')})
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <FormControlLabel 
                      control={
                        <Switch 
                          checked={globalPasswordConfig.enabled} 
                          onChange={async (e) => {
                            const newConfig = { ...globalPasswordConfig, enabled: e.target.checked };
                            await updatePasswordConfig(newConfig);
                          }}
                          disabled={passwordConfigLoading}
                        />
                      } 
                      label={t('postReleaseTesting.passwordProtection.enablePassword', 'Enable Password Protection')} 
                    />
                    {globalPasswordConfig.enabled && (
                      <TextField
                        size="small"
                        label={t('postReleaseTesting.passwordProtection.setPassword', 'Set Password')}
                        type="password"
                        value={globalPasswordConfig.password}
                        onChange={async (e) => {
                          const newConfig = { ...globalPasswordConfig, password: e.target.value };
                          await updatePasswordConfig(newConfig);
                        }}
                        sx={{ width: 200 }}
                        placeholder={t('postReleaseTesting.passwordProtection.defaultPassword', 'Default: post-release-2024')}
                        helperText={t('postReleaseTesting.passwordProtection.helperText', 'After setting, all users need password confirmation for START TEST and Save Options')}
                        disabled={passwordConfigLoading}
                      />
                    )}
                    {passwordConfigLoading && (
                      <CircularProgress size={20} />
                    )}
                  </Box>
                </Box>
              )}

              {/* 非Admin用户提示 */}
              {user && user.role !== 'admin' && (
                <Box mb={2} p={2} sx={{ backgroundColor: '#f8d7da', borderRadius: 2, border: '1px solid #f5c6cb' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#721c24' }}>
                    ⚠️ {t('postReleaseTesting.permissionHint.title', 'Permission Hint')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('postReleaseTesting.permissionHint.description', 'Your current role is: {{role}}. Only Admin users can set password protection features.', { role: user.role })}
                  </Typography>
                </Box>
              )}

              {/* 并发/设备选择区域 */}
              <Box mb={2}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>Run Options</Typography>
                
                {hasPermission('post-release', 'start_test') || user?.role === 'admin' ? (
                  <>
                    <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(7, minmax(0, 1fr))' }} gap={3} alignItems="center">
                      <TextField size="small" label="Max Device Concurrency" type="number" inputProps={{ min: 1, max: 10 }} value={maxDevices} onChange={(e:any)=>setMaxDevices(parseInt(e.target.value||'1',10))} />
                      <TextField size="small" label="Integration Concurrency (per device)" type="number" inputProps={{ min: 1, max: 10 }} value={integrationConcurrency} onChange={(e:any)=>setIntegrationConcurrency(parseInt(e.target.value||'1',10))} />
                      <TextField size="small" label="Retries" type="number" inputProps={{ min: 0, max: 5 }} value={retries} onChange={(e:any)=>setRetries(parseInt(e.target.value||'0',10))} />
                      <FormControlLabel control={<Switch size="small" checked={continueOnFail} onChange={(e)=>setContinueOnFail(e.target.checked)} />} label="Continue On Fail" />
                      <FormControlLabel control={<Switch size="small" checked={autoValidate} onChange={(e)=>setAutoValidate(e.target.checked)} />} label="Auto-run SQL Validation" />
                      <TextField size="small" label="Validation Concurrency" type="number" inputProps={{ min: 1, max: 20 }} value={validationConcurrency} onChange={(e:any)=>setValidationConcurrency(parseInt(e.target.value||'1',10))} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
                        <FormControl size="small" fullWidth>
                          <InputLabel id="start-mode-label">Start Mode</InputLabel>
                          <Select labelId="start-mode-label" label="Start Mode" value={startMode} onChange={(e)=>setStartMode(e.target.value as any)}>
                            <MenuItem value="reuse">reuse</MenuItem>
                            <MenuItem value="reject">reject</MenuItem>
                            <MenuItem value="allow">allow</MenuItem>
                          </Select>
                        </FormControl>
                        <Tooltip
                          arrow
                          title={
                            <Box>
                              <Typography variant="caption"><b>reuse</b>: reuse recent run within debounce window</Typography><br/>
                              <Typography variant="caption"><b>reject</b>: deny when active runs reach limit</Typography><br/>
                              <Typography variant="caption"><b>allow</b>: reuse latest run when limit reached</Typography>
                            </Box>
                          }
                        >
                          <IconButton size="small"><InfoIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </Box>
                      <TextField size="small" label="Debounce (ms)" type="number" inputProps={{ min: 0, max: 10000 }} value={debounceWindowMs} onChange={(e:any)=>setDebounceWindowMs(parseInt(e.target.value||'0',10))} />
                      <TextField size="small" label="Max Active Runs" type="number" inputProps={{ min: 1, max: 10 }} value={maxActiveRuns} onChange={(e:any)=>setMaxActiveRuns(parseInt(e.target.value||'1',10))} />
                    </Box>
                    <Button 
                      variant="outlined" 
                      sx={{ textTransform: 'none', mt: 2 }} 
                      onClick={handleSaveConfigWithPassword}
                    >
                      Save Options
                    </Button>
                  </>
                ) : (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {t('postReleaseTesting.runOptions.noPermission', 'You do not have permission to configure Post-Release test options. Please contact the administrator.')}
                  </Alert>
                )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption">Select Devices</Typography>
                      <Box display="flex" gap={1}>
                      {serverConfig?.devices && Object.keys(serverConfig.devices)
                        .sort((a: string, b: string) => {
                          const pref: string[] = (serverConfig as any)?.preferredDevices || [];
                          console.log('🔧 [DEBUG] Sorting devices, preferredDevices:', pref);
                          console.log('🔧 [DEBUG] Current selectedDevices:', selectedDevices);
                          const ia = pref.indexOf(a);
                          const ib = pref.indexOf(b);
                          if (ia !== ib) {
                            const va = ia === -1 ? Number.MAX_SAFE_INTEGER : ia;
                            const vb = ib === -1 ? Number.MAX_SAFE_INTEGER : ib;
                            return va - vb;
                          }
                          return a.localeCompare(b);
                        })
                        .map((id: string) => (
                        <Chip key={id} label={id} color={selectedDevices.includes(id) ? 'primary' : 'default'} onClick={() => {
                          console.log('🔧 [DEBUG] Clicking device:', id, 'current selected:', selectedDevices);
                          setSelectedDevices(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]);
                        }} />
                      ))}
                      </Box>
                    </Box>
                  <Button 
                    variant="contained" 
                    startIcon={<PlaylistPlayIcon />} 
                    disabled={testing || selectedDevices.length === 0} 
                    onClick={handleStartTestWithPassword} 
                    sx={{ px: 3, py: 1.2, borderRadius: 2, boxShadow: '0 8px 16px rgba(25,118,210,.25)', ml: 'auto' }}
                  >
                      {t('postReleaseTesting.startTest')}
                    </Button>
                  </Box>
                </Box>

              {/* 🎯 集成选择器 */}
              {availableIntegrations.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <IntegrationSelector
                    integrations={availableIntegrations}
                    selectedIntegrations={selectedIntegrations}
                    onChange={setSelectedIntegrations}
                    disabled={testing}
                  />
                </Box>
              )}

              {/* 设备列表（可点击选择） */}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {serverConfig?.devices && Object.entries(serverConfig.devices).map(([deviceId, integrations]: any) => {
                  const selected = selectedDevices.includes(deviceId);
                  return (
                    <Grid item xs={12} md={4} key={deviceId}>
                      <Paper
                        onClick={() => setSelectedDevices(prev => prev.includes(deviceId) ? prev.filter(d => d !== deviceId) : [...prev, deviceId])}
                        sx={{ p: 2, borderRadius: 2, cursor: 'pointer', transition: 'all .2s', border: selected ? '2px solid #1976d2' : '1px solid #e0e0e0', '&:hover': { boxShadow: '0 6px 14px rgba(0,0,0,.08)' } }}
                      >
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {deviceId}
                          {selected && <Chip label="selected" color="primary" size="small" />}
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {integrations.map((it: string, idx: number) => (
                            <Chip key={idx} label={it} size="small" color="primary" variant="outlined" />
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* 🎯 历史运行记录 */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 6px 12px rgba(0,0,0,.06)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  📋 测试运行历史 | Test Run History
                </Typography>
                <Button 
                  size="small" 
                  variant="outlined"
                  onClick={loadHistoricalRuns}
                >
                  刷新 | Refresh
                </Button>
              </Box>

              {historicalRunsError ? (
                <Alert 
                  severity="error" 
                  action={
                    <Button size="small" onClick={() => {
                      setHistoricalRunsError(null);
                      loadHistoricalRuns();
                    }}>
                      重试 | Retry
                    </Button>
                  }
                  onClose={() => setHistoricalRunsError(null)}
                >
                  {historicalRunsError}
                </Alert>
              ) : historicalRuns.length === 0 ? (
                <Alert severity="info">暂无历史记录 | No historical runs yet</Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {historicalRuns.map((run: any) => {
                    // 计算验证统计
                    const items = run.items || [];
                    const validationStats = {
                      passed: items.filter((i: any) => i.validation?.status === 'passed').length,
                      failed: items.filter((i: any) => i.validation?.status === 'failed').length,
                      error: items.filter((i: any) => i.validation?.status === 'error').length,
                      noOrderId: items.filter((i: any) => i.validation?.status === 'no_order_id').length,
                    };
                    const hasValidation = items.some((i: any) => i.validation);
                    
                    return (
                      <Paper key={run.id} sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                          <Box flex={1}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Run ID: {run.id}
                            </Typography>
                            <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                              <Chip 
                                label={`${run.status} (${run.status})`} 
                                size="small" 
                                color={run.status === 'completed' ? 'success' : run.status === 'failed' ? 'error' : 'default'}
                              />
                              <Chip 
                                label={`${items.length} 测试项`} 
                                size="small" 
                                variant="outlined"
                              />
                              <Chip 
                                label={new Date(run.startedAt).toLocaleString()} 
                                size="small" 
                                variant="outlined"
                              />
                            </Box>
                            
                            {/* SQL验证统计 */}
                            {hasValidation && (
                              <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                                <Typography variant="caption" sx={{ mr: 1, fontWeight: 600 }}>
                                  SQL验证:
                                </Typography>
                                {validationStats.passed > 0 && (
                                  <Chip 
                                    icon={<CheckCircleIcon />}
                                    label={`✅ ${validationStats.passed}`}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                  />
                                )}
                                {validationStats.failed > 0 && (
                                  <Chip 
                                    icon={<CancelIcon />}
                                    label={`❌ ${validationStats.failed}`}
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                  />
                                )}
                                {validationStats.error > 0 && (
                                  <Chip 
                                    icon={<ErrorIcon />}
                                    label={`⚠️ ${validationStats.error}`}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                  />
                                )}
                                {validationStats.noOrderId > 0 && (
                                  <Chip 
                                    icon={<HelpOutlineIcon />}
                                    label={`⚠️ ${validationStats.noOrderId}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            )}
                          </Box>
                          
                          <Box display="flex" gap={1}>
                            {hasValidation && (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<ReceiptIcon />}
                                onClick={() => {
                                  setSelectedHistoricalRun(run);
                                  setHistoricalReportOpen(true);
                                }}
                              >
                                查看SQL验证报告
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              href={`${API_ENDPOINTS.API_BASE}/api/post-release/run/${run.id}/report.html`}
                              target="_blank"
                            >
                              HTML Report
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              href={`${API_ENDPOINTS.API_BASE}/api/post-release/run/${run.id}/logs.txt`}
                              target="_blank"
                            >
                              下载日志
                            </Button>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 测试进行中对话框 */}
      <Dialog
        open={testDialogOpen}
        onClose={(e, reason) => {
          // 运行中：允许通过遮罩/Esc最小化到后台，而不是强制停止
          if (testing && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
            setTestDialogOpen(false);
            setRunBannerVisible(true);
            return;
          }
          // 非运行中或显式关闭：执行停止与清理
          stopTest();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <PlaylistPlayIcon />
              {t('postReleaseTesting.testInProgress')} - {selectedAPK}
            </Box>
            {viewingRunId && viewingRunId !== currentRunId && (
              <Chip 
                label="历史记录 | Historical Run" 
                size="small" 
                color="info" 
                variant="outlined"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              {t('postReleaseTesting.testProgress')}
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Box flex={1}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">
                    {currentStep} / {config?.testOrder?.length || 0} {t('postReleaseTesting.steps')}
                  </Typography>
                  <Typography variant="body2">
                    {Math.round(testProgress)}%
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: 8,
                    backgroundColor: 'grey.300',
                    borderRadius: 4,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      width: `${testProgress}%`,
                      height: '100%',
                      backgroundColor: 'primary.main',
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* 设备锁排队提示 */}
          {Object.keys(queuedByDevice).length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Device queued by lock: {Object.entries(queuedByDevice).map(([d, n]) => `${d}(${n})`).join(', ')}
              </Typography>
            </Alert>
          )}

          {/* 🎯 SQL验证报告汇总 */}
          {runs && runs.length > 0 && (() => {
            const currentRun = runs[0];
            const items = currentRun?.items || [];
            const validationStats = {
              total: items.length,
              passed: items.filter((i: any) => i.validation?.status === 'passed').length,
              failed: items.filter((i: any) => i.validation?.status === 'failed').length,
              error: items.filter((i: any) => i.validation?.status === 'error').length,
              noOrderId: items.filter((i: any) => i.validation?.status === 'no_order_id').length,
              validating: items.filter((i: any) => i.validation?.status === 'validating').length,
            };
            
            // ✅ 始终显示SQL验证报告区域（即使还没有验证数据）
            const hasValidationData = items.some((i: any) => i.validation);
            
            return (
              <Card sx={{ mb: 3, border: '2px solid #e0e0e0' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <ReceiptIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      📊 SQL验证报告 | SQL Validation Report
                    </Typography>
                  </Box>
                  
                  {hasValidationData ? (
                    <>
                      {/* 验证结果统计 */}
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>验证结果统计:</Typography>
                      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                        <Chip 
                          icon={<CheckCircleIcon />}
                          label={`✅ 验证通过: ${validationStats.passed}`}
                          color="success"
                          variant={validationFilter === 'passed' ? 'filled' : 'outlined'}
                          onClick={() => setValidationFilter(validationFilter === 'passed' ? 'all' : 'passed')}
                        />
                        <Chip 
                          icon={<CancelIcon />}
                          label={`❌ 验证失败: ${validationStats.failed}`}
                          color="error"
                          variant={validationFilter === 'failed' ? 'filled' : 'outlined'}
                          onClick={() => setValidationFilter(validationFilter === 'failed' ? 'all' : 'failed')}
                        />
                        <Chip 
                          icon={<ErrorIcon />}
                          label={`⚠️  验证错误: ${validationStats.error}`}
                          color="warning"
                          variant={validationFilter === 'error' ? 'filled' : 'outlined'}
                          onClick={() => setValidationFilter(validationFilter === 'error' ? 'all' : 'error')}
                        />
                        <Chip 
                          icon={<HelpOutlineIcon />}
                          label={`⚠️  未提取Order ID: ${validationStats.noOrderId}`}
                          color="default"
                          variant={validationFilter === 'no_order_id' ? 'filled' : 'outlined'}
                          onClick={() => setValidationFilter(validationFilter === 'no_order_id' ? 'all' : 'no_order_id')}
                        />
                        {validationStats.validating > 0 && (
                          <Chip 
                            label={`⏳ 验证中: ${validationStats.validating}`}
                            color="info"
                          />
                        )}
                      </Box>
                      
                      {/* 搜索过滤器 */}
                      <TextField
                        size="small"
                        placeholder="搜索 Order ID..."
                        value={orderIdSearch}
                        onChange={(e) => setOrderIdSearch(e.target.value)}
                        sx={{ width: 300 }}
                        InputProps={{
                          startAdornment: '🔍'
                        }}
                      />
                    </>
                  ) : (
                    // ✅ 如果还没有验证数据，显示等待提示
                    <Box textAlign="center" py={2}>
                      <Typography variant="body2" color="text.secondary">
                        ⏳ 等待测试完成后生成SQL验证报告...
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* 简化展示：设备/集成/环境队列 */}
          <Box>
            {runs.map((run) => {
              // 过滤逻辑
              const filteredItems = (run.items || []).filter((it: any) => {
                // ✅ 修复：正在运行或排队的测试不应被验证状态过滤
                const isRunningOrPending = ['running', 'pending', 'queued'].includes(it.status);
                
                // 验证状态过滤（仅对已完成/失败的测试应用）
                if (!isRunningOrPending && validationFilter !== 'all' && it.validation?.status !== validationFilter) {
                  return false;
                }
                // Order ID搜索
                if (orderIdSearch && !it.orderId?.toLowerCase().includes(orderIdSearch.toLowerCase())) {
                  return false;
                }
                return true;
              });
              
              return (
                <Box key={run.id} sx={{ mb: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle1">Run: {run.id} - {run.status}</Typography>
                    <Button size="small" variant="outlined" href={`${API_ENDPOINTS.API_BASE}/api/post-release/run/${run.id}/logs.txt`} target="_blank">Download all logs</Button>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Devices: {(run.items||[]).length > 0 ? Array.from(new Set(run.items.map((i:any)=>i.deviceId))).join(', ') : '-'}
                    {validationFilter !== 'all' || orderIdSearch ? ` (显示 ${filteredItems.length}/${(run.items||[]).length} 项)` : ''}
                  </Typography>
                  
                  {filteredItems.map((it: any, idx: number) => {
                    const originalIdx = run.items.indexOf(it);
                    // 🎯 生成唯一的锚点ID用于滚动定位
                    const itemAnchorId = `item-${it.deviceId}-${it.integration}-${it.environment}`;
                    return (
                      <Box 
                        key={originalIdx} 
                        id={itemAnchorId}
                        sx={{ mb: .5, scrollMarginTop: '100px' }}
                      >
                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" sx={{ fontSize: 14 }}>
                          <Chip label={it.deviceId} size="small" />
                          <Chip label={it.integration} size="small" color="primary" />
                          <Chip label={it.environment} size="small" />
                          <Chip label={it.status} size="small" color={it.status === 'completed' ? 'success' : it.status === 'failed' ? 'error' : (it.status === 'running' ? 'primary' : 'default')} />
                          
                          {/* 🎯 Order ID显示 */}
                          {it.orderId && (
                            <Chip 
                              icon={<ReceiptIcon />}
                              label={`Order: ${it.orderId}`} 
                              size="small" 
                              variant="outlined"
                              color="info"
                            />
                          )}
                          
                          {/* 🎯 SQL验证状态显示 */}
                          {it.validation && (
                            <>
                              {it.validation.status === 'passed' && (
                                <Chip 
                                  icon={<VerifiedUserIcon />}
                                  label="✅ SQL验证通过" 
                                  size="small" 
                                  color="success"
                                  onClick={() => {
                                    setSelectedValidation(it.validation);
                                    setValidationDetailsOpen(true);
                                  }}
                                  sx={{ cursor: 'pointer' }}
                                />
                              )}
                              {it.validation.status === 'failed' && (
                                <Chip 
                                  icon={<CancelIcon />}
                                  label="❌ SQL验证失败" 
                                  size="small" 
                                  color="error"
                                  onClick={() => {
                                    setSelectedValidation(it.validation);
                                    setValidationDetailsOpen(true);
                                  }}
                                  sx={{ cursor: 'pointer' }}
                                />
                              )}
                              {it.validation.status === 'error' && (
                                <Chip 
                                  icon={<ErrorIcon />}
                                  label="⚠️  验证错误" 
                                  size="small" 
                                  color="warning"
                                  onClick={() => {
                                    setSelectedValidation(it.validation);
                                    setValidationDetailsOpen(true);
                                  }}
                                  sx={{ cursor: 'pointer' }}
                                />
                              )}
                              {it.validation.status === 'no_order_id' && (
                                <Chip 
                                  icon={<HelpOutlineIcon />}
                                  label="⚠️  未提取Order ID" 
                                  size="small" 
                                  color="default"
                                  onClick={() => {
                                    setSelectedValidation(it.validation);
                                    setValidationDetailsOpen(true);
                                  }}
                                  sx={{ cursor: 'pointer' }}
                                />
                              )}
                              {it.validation.status === 'validating' && (
                                <Chip 
                                  label="⏳ 验证中..." 
                                  size="small" 
                                  color="info"
                                />
                              )}
                            </>
                          )}
                          
                          {it.status === 'queued' && (
                            <Chip label="queued by device lock" size="small" />
                          )}
                          {typeof it.attempts === 'number' && (<Chip label={`attempts:${it.attempts}`} size="small" />)}
                          {it.lastUpdatedAt && (<Chip label={new Date(it.lastUpdatedAt).toLocaleTimeString()} size="small" />)}
                        </Box>
                        {/* inline logs */}
                        <Box sx={{ pl: 1.5, mt: .25 }}>
                          {Array.isArray(it.logs) && it.logs.length > 0 && (
                            <>
                              {it.logs.slice(-5).map((lg: any, ix: number) => (
                                <Box key={ix} sx={{ fontSize: 12, color: lg.level === 'error' ? 'error.main' : 'text.secondary' }}>
                                  [{new Date(lg.ts).toLocaleTimeString()}] (#{lg.attempt}) {lg.level.toUpperCase()}: {lg.message}
                                </Box>
                              ))}
                              <Button size="small" variant="text" onClick={async () => {
                                const { data } = await axios.get(`${API_ENDPOINTS.API_BASE}/api/post-release/run/${run.id}/item/${originalIdx}/logs`);
                                const text = Array.isArray(data) ? data.map((lg: any) => `[${lg.ts}] (#${lg.attempt}) ${lg.level.toUpperCase()}: ${lg.message}`).join('\n') : JSON.stringify(data, null, 2);
                                setLogTitle(`${it.deviceId} / ${it.integration} / ${it.environment}`);
                                setLogText(text);
                                setLogOpen(true);
                              }}>View logs</Button>
                            </>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          {/* 🎯 如果是查看历史run，显示提示 */}
          {viewingRunId && viewingRunId !== currentRunId && (
            <Alert severity="info" sx={{ flex: 1, mr: 2 }}>
              ℹ️ 这是历史运行记录，Stop按钮不会影响当前运行的测试
            </Alert>
          )}
          {testing && viewingRunId === currentRunId && (
            <Button onClick={() => { setTestDialogOpen(false); setRunBannerVisible(true); }}>
              {t('postReleaseTesting.runInBackground')}
            </Button>
          )}
          <Button 
            onClick={stopTest} 
            startIcon={<StopIcon />}
            disabled={viewingRunId !== currentRunId && currentRunId !== null}
          >
            {testProgress === 100 ? t('postReleaseTesting.complete') : t('postReleaseTesting.stopTest')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 运行中提示条（可恢复弹窗） */}
      {runBannerVisible && currentRunId && !testDialogOpen && (
        <Alert severity="info" sx={{ position: 'fixed', bottom: 12, right: 12, zIndex: 1200, maxWidth: 520 }} onClose={() => setRunBannerVisible(false)}>
          <Box display="flex" alignItems="center" gap={1}>
            <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>Run {currentRunId} - {bannerRunStatus}</span>
            <Button size="small" variant="outlined" onClick={() => setTestDialogOpen(true)}>{t('common.open') || 'Open'}</Button>
          </Box>
        </Alert>
      )}
      {/* 全量日志对话框 */}
      <Dialog open={logOpen} onClose={() => setLogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{logTitle}</DialogTitle>
        <DialogContent>
          <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{logText}</Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* 🎯 SQL验证详情对话框 */}
      <Dialog 
        open={validationDetailsOpen} 
        onClose={() => setValidationDetailsOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ReceiptIcon color="primary" />
            SQL验证详情 | SQL Validation Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedValidation && (
            <Box>
              {/* 验证状态 */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: 
                selectedValidation.status === 'passed' ? '#e8f5e9' : 
                selectedValidation.status === 'failed' ? '#ffebee' : 
                selectedValidation.status === 'error' ? '#fff3e0' : 
                selectedValidation.status === 'validating' ? '#e3f2fd' : '#f5f5f5'
              }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {selectedValidation.status === 'passed' && <CheckCircleIcon color="success" />}
                  {selectedValidation.status === 'failed' && <CancelIcon color="error" />}
                  {selectedValidation.status === 'error' && <ErrorIcon color="warning" />}
                  {selectedValidation.status === 'no_order_id' && <HelpOutlineIcon />}
                  {selectedValidation.status === 'validating' && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={20} /> 验证中...</Box>}
                  {selectedValidation.status !== 'validating' && <>验证状态 | Validation Status: <strong>{selectedValidation.status}</strong></>}
                </Typography>
                
                {/* 验证摘要 */}
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                  {selectedValidation.message}
                </Typography>
                
                {/* 时间戳和耗时 */}
                {selectedValidation.status !== 'validating' && (
                  <Box display="flex" gap={2} sx={{ fontSize: 14, color: 'text.secondary' }}>
                    {selectedValidation.timestamp && (
                      <Typography variant="body2">
                        完成时间 | Completed: {new Date(selectedValidation.timestamp).toLocaleString()}
                      </Typography>
                    )}
                    {selectedValidation.durationMs && (
                      <Typography variant="body2">
                        耗时 | Duration: {selectedValidation.durationMs}ms
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
              
              {/* 🎯 改进：结构化展示详细信息 */}
              {selectedValidation.details && selectedValidation.status !== 'validating' && (() => {
                const details = selectedValidation.details;
                const lines = details.split('\n');
                
                // 解析details内容
                const sections: { title?: string; items: string[] }[] = [];
                let currentSection: { title?: string; items: string[] } = { items: [] };
                
                for (const line of lines) {
                  if (line.includes('订单ID') || line.includes('Order ID')) {
                    sections.push({ title: '订单信息 | Order Info', items: [line] });
                  } else if (line.includes('验证环境') || line.includes('Environment')) {
                    if (currentSection.items.length > 0) sections.push(currentSection);
                    currentSection = { items: [line] };
                  } else if (line.includes('执行的验证项目') || line.includes('Executed validation')) {
                    if (currentSection.items.length > 0) sections.push(currentSection);
                    currentSection = { title: '🔍 执行的验证项目 | Executed Validation Items', items: [] };
                  } else if (line.includes('验证结果详情') || line.includes('Validation result')) {
                    if (currentSection.items.length > 0) sections.push(currentSection);
                    currentSection = { title: '📊 验证结果详情 | Validation Result Details', items: [] };
                  } else if (line.trim()) {
                    currentSection.items.push(line);
                  }
                }
                if (currentSection.items.length > 0) sections.push(currentSection);
                
                return (
                  <Box>
                    {sections.map((section, sectionIndex) => (
                      <Paper key={sectionIndex} sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
                        {section.title && (
                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                            {section.title}
                          </Typography>
                        )}
                        <Box component="ul" sx={{ m: 0, pl: 2, listStyleType: 'none' }}>
                          {section.items.map((item, itemIndex) => (
                            <Box 
                              component="li" 
                              key={itemIndex} 
                              sx={{ 
                                py: 0.5,
                                fontSize: 14,
                                lineHeight: 1.6,
                                fontFamily: item.startsWith('  ') ? 'monospace' : 'inherit',
                                color: item.includes('✅') ? 'success.main' : 
                                       item.includes('❌') ? 'error.main' : 
                                       item.includes('⚠️') ? 'warning.main' : 'text.primary'
                              }}
                            >
                              {item}
                            </Box>
                          ))}
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                );
              })()}
              
              {/* Validation ID */}
              {selectedValidation.validationId && (
                <Box sx={{ mt: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Validation ID: {selectedValidation.validationId}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationDetailsOpen(false)}>关闭 | Close</Button>
        </DialogActions>
      </Dialog>

      {/* 🎯 历史SQL验证报告对话框 */}
      <Dialog 
        open={historicalReportOpen} 
        onClose={() => setHistoricalReportOpen(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ReceiptIcon color="primary" />
            SQL验证报告 | SQL Validation Report
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedHistoricalRun && (() => {
            const items = selectedHistoricalRun.items || [];
            const validationStats = {
              total: items.length,
              passed: items.filter((i: any) => i.validation?.status === 'passed').length,
              failed: items.filter((i: any) => i.validation?.status === 'failed').length,
              error: items.filter((i: any) => i.validation?.status === 'error').length,
              noOrderId: items.filter((i: any) => i.validation?.status === 'no_order_id').length,
            };
            
            return (
              <Box>
                {/* 运行信息 */}
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Run ID: {selectedHistoricalRun.id}
                  </Typography>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    <Chip label={selectedHistoricalRun.status} size="small" color={selectedHistoricalRun.status === 'completed' ? 'success' : 'error'} />
                    <Chip label={new Date(selectedHistoricalRun.startedAt).toLocaleString()} size="small" variant="outlined" />
                    {selectedHistoricalRun.finishedAt && (
                      <Chip 
                        label={`耗时: ${Math.round((new Date(selectedHistoricalRun.finishedAt).getTime() - new Date(selectedHistoricalRun.startedAt).getTime()) / 1000)}s`} 
                        size="small" 
                        variant="outlined" 
                      />
                    )}
                  </Box>
                </Paper>
                
                {/* 验证统计 */}
                <Paper sx={{ p: 2, mb: 2, border: '2px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    验证结果统计 | Validation Statistics:
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Chip 
                      icon={<CheckCircleIcon />}
                      label={`✅ 验证通过: ${validationStats.passed}`}
                      color="success"
                    />
                    <Chip 
                      icon={<CancelIcon />}
                      label={`❌ 验证失败: ${validationStats.failed}`}
                      color="error"
                    />
                    <Chip 
                      icon={<ErrorIcon />}
                      label={`⚠️ 验证错误: ${validationStats.error}`}
                      color="warning"
                    />
                    <Chip 
                      icon={<HelpOutlineIcon />}
                      label={`⚠️ 未提取Order ID: ${validationStats.noOrderId}`}
                      color="default"
                    />
                  </Box>
                </Paper>
                
                {/* 详细结果 */}
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  详细结果 | Detailed Results:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 500, overflow: 'auto' }}>
                  {items.map((item: any, idx: number) => (
                    <Paper key={idx} sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                        <Box flex={1}>
                          <Box display="flex" gap={0.5} flexWrap="wrap" mb={0.5}>
                            <Chip label={item.deviceId} size="small" />
                            <Chip label={item.integration} size="small" color="primary" />
                            <Chip label={item.environment} size="small" />
                            <Chip 
                              label={item.status} 
                              size="small" 
                              color={item.status === 'completed' ? 'success' : item.status === 'failed' ? 'error' : 'default'}
                            />
                          </Box>
                          
                          {item.orderId && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              <strong>Order ID:</strong> {item.orderId}
                            </Typography>
                          )}
                          
                          {item.validation && (
                            <Box mt={1}>
                              {item.validation.status === 'passed' && (
                                <Box display="flex" alignItems="center" gap={0.5} sx={{ color: 'success.main' }}>
                                  <VerifiedUserIcon fontSize="small" />
                                  <Typography variant="body2">
                                    <strong>✅ SQL验证通过</strong> - {item.validation.message}
                                  </Typography>
                                </Box>
                              )}
                              {item.validation.status === 'failed' && (
                                <Box display="flex" alignItems="center" gap={0.5} sx={{ color: 'error.main' }}>
                                  <CancelIcon fontSize="small" />
                                  <Typography variant="body2">
                                    <strong>❌ SQL验证失败</strong> - {item.validation.message}
                                  </Typography>
                                </Box>
                              )}
                              {item.validation.status === 'error' && (
                                <Box display="flex" alignItems="center" gap={0.5} sx={{ color: 'warning.main' }}>
                                  <ErrorIcon fontSize="small" />
                                  <Typography variant="body2">
                                    <strong>⚠️ 验证错误</strong> - {item.validation.message}
                                  </Typography>
                                </Box>
                              )}
                              {item.validation.status === 'no_order_id' && (
                                <Box display="flex" alignItems="center" gap={0.5} sx={{ color: 'text.secondary' }}>
                                  <HelpOutlineIcon fontSize="small" />
                                  <Typography variant="body2">
                                    <strong>⚠️ 未提取Order ID</strong> - {item.validation.message}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                        
                        {item.validation && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={async () => {
                              setSelectedValidation(item.validation);
                              setValidationDetailsOpen(true);
                              
                              // 🎯 如果状态是validating，触发实际验证
                              if (item.validation.status === 'validating' && item.orderId && selectedHistoricalRun?.id) {
                                console.log('🔄 [PostRelease] 检测到validating状态，触发验证...');
                                try {
                                  const response = await axios.post(`${API_ENDPOINTS.API_BASE}/api/validation/run`, {
                                    env: item.environment,
                                    orderIds: [item.orderId],
                                    runId: selectedHistoricalRun.id,
                                    options: { concurrency: 1 }
                                  });
                                  console.log('✅ [PostRelease] 验证已触发:', response.data);
                                  
                                  // 🎯 轮询直到验证完成（最多30秒）
                                  let attempts = 0;
                                  const maxAttempts = 15;
                                  const pollInterval = setInterval(async () => {
                                    attempts++;
                                    try {
                                      const { data: updatedRun } = await axios.get(`${API_ENDPOINTS.API_BASE}/api/post-release/run/${selectedHistoricalRun.id}`);
                                      const updatedItem = updatedRun.items?.find((i: any) => i.orderId === item.orderId);
                                      
                                      if (updatedItem?.validation && updatedItem.validation.status !== 'validating') {
                                        // 验证完成
                                        console.log('✅ [PostRelease] 验证完成:', updatedItem.validation.status);
                                        setSelectedHistoricalRun(updatedRun);
                                        setSelectedValidation(updatedItem.validation);
                                        clearInterval(pollInterval);
                                      } else if (attempts >= maxAttempts) {
                                        // 超时
                                        console.warn('⏱️ [PostRelease] 验证轮询超时');
                                        clearInterval(pollInterval);
                                      }
                                    } catch (e) {
                                      console.error('刷新run数据失败:', e);
                                      clearInterval(pollInterval);
                                    }
                                  }, 2000);
                                } catch (error) {
                                  console.error('❌ [PostRelease] 触发验证失败:', error);
                                }
                              }
                            }}
                          >
                            查看详情
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoricalReportOpen(false)}>关闭 | Close</Button>
        </DialogActions>
      </Dialog>

      {/* 并发限制对话框 */}
      <ConcurrencyLimitDialog
        open={concurrencyDialogOpen}
        onClose={() => {}} // 不提供关闭功能，防止意外关闭
        onAttach={handleConcurrencyDialogAttach}
        maxActiveRuns={concurrencyDialogData?.maxActiveRuns || 1}
        activeRunId={concurrencyDialogData?.activeRunId || ''}
        mode={concurrencyDialogData?.mode || 'reuse'}
      />

      {/* 🔐 密码确认对话框 */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={() => {
          setPasswordDialogOpen(false);
          setPasswordInput('');
          setPasswordError('');
          setPendingAction(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <VerifiedUserIcon color="primary" />
            <Typography variant="h6">
              {t('postReleaseTesting.passwordConfirmation.title', 'Password Confirmation')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('postReleaseTesting.passwordConfirmation.description', 'For security reasons, please enter your password to confirm this operation:')}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={t('postReleaseTesting.passwordConfirmation.passwordLabel', 'Password')}
            type="password"
            fullWidth
            variant="outlined"
            value={passwordInput}
            onChange={(e) => {
              setPasswordInput(e.target.value);
              setPasswordError('');
            }}
            error={!!passwordError}
            helperText={passwordError || t('postReleaseTesting.passwordConfirmation.passwordHint', 'Please enter the Post-Release test password')}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handlePasswordConfirm();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setPasswordDialogOpen(false);
              setPasswordInput('');
              setPasswordError('');
              setPendingAction(null);
            }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button 
            onClick={handlePasswordConfirm}
            variant="contained"
            disabled={!passwordInput.trim()}
          >
            {t('common.confirm', 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PostReleaseTesting;
